<script setup lang="ts">
import IconAngleLeft from '@/assets/icons/angle-left.svg'
import IconAngleRight from '@/assets/icons/angle-right.svg'

const props = defineProps({
  totalPages: {
    type: Number,
    required: true,
  },
  initialPage: {
    type: Number,
    default: 1,
  },
  onPageNumberChange: {
    type: Function,
    required: false,
  },
  scrollOnChange: {
    type: Boolean,
    default: false,
  },
})

const pageCounter = ref(props.initialPage)

function setInitialPage(totalPages) {
  if (totalPages && props.initialPage >= totalPages) {
    pageCounter.value = Number(totalPages)
    return
  }

  if (props.initialPage <= 0) {
    pageCounter.value = 1
  }
}

watch(() => props.totalPages, setInitialPage)

const thereIsNextPage = computed(() => pageCounter.value < props.totalPages)
const thereIsPrevPage = computed(() => pageCounter.value > 1)

function animateOnInvalidInput(input: HTMLInputElement) {
  const invalidInput = [
    { borderColor: 'red', outlineColor: 'red' },
    { borderColor: 'black', outlineColor: 'black' },
  ]

  const timing = {
    duration: 1000,
    iterations: 1,
  }

  input.animate(invalidInput, timing)
}

function onEnter(input: HTMLInputElement) {
  const value = Number(input.value)

  if (Number.isInteger(value) && value <= props.totalPages && value >= 1) {
    if (value !== pageCounter.value) {
      pageCounter.value = value
      return
    }
  } else {
    animateOnInvalidInput(input)
  }

  input.value = pageCounter.value.toString()
}

watch(
  () => pageCounter.value,
  (newValue) => {
    if (props.onPageNumberChange) {
      props.onPageNumberChange(Number(newValue))
    }

    if (typeof window !== 'undefined' && props.scrollOnChange) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  },
)
</script>

<template>
  <nav
    class="mt-10 flex items-center justify-end gap-x-2 pb-20 lg:pb-36 [&>*]:text-sm"
  >
    <p>Page</p>
    <input
      data-testid="page-number-input"
      type="text"
      :value="pageCounter"
      class="h-7 w-10 rounded border border-s-600 py-1 text-center"
      @keyup.enter="onEnter($event.target as HTMLInputElement)"
    />
    <p>
      of <span data-testid="total-pages-counter">{{ totalPages }}</span>
    </p>

    <button
      data-testid="prev-page-link"
      class="flex h-6 w-6 items-center justify-center rounded-full text-white"
      :class="thereIsPrevPage ? 'bg-s-900' : 'bg-s-400'"
      :disabled="!thereIsPrevPage"
      @click="pageCounter--"
    >
      <IconAngleLeft class="h-6 w-6" />
    </button>
    <button
      data-testid="next-page-link"
      class="flex h-6 w-6 items-center justify-center rounded-full text-white"
      :class="thereIsNextPage ? 'bg-s-900' : 'bg-s-400'"
      :disabled="!thereIsNextPage"
      @click="pageCounter++"
    >
      <IconAngleRight class="h-6 w-6" />
    </button>
  </nav>
</template>
